import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextareaAutosize,
  Divider,
  Paper,
} from '@mui/material';
import {
  X,
  CaretDown,
  CaretUp,
  TrashSimple,
  Plus,
} from '@phosphor-icons/react';
import { useAlert } from '../../hooks/useAlert';

interface QuestionnaireDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedStore: {
    id: string;
    friendly_name: string;
    existingQuestionsCount: number;
  };
  onSave: (questions: Question[]) => void;
  isPending: boolean;
}

interface Question {
  question: string;
  answer: string;
  isOpen: boolean;
}

const QuestionnaireDrawer: React.FC<QuestionnaireDrawerProps> = ({
  open,
  onClose,
  selectedStore,
  onSave,
  isPending,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [openQuestionIndex, setOpenQuestionIndex] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const { showAlert } = useAlert();

  useEffect(() => {
    if (open) {
      setQuestions([{ question: '', answer: '', isOpen: true }]);
      setOpenQuestionIndex(0);
    } else {
      setQuestions([]);
      setOpenQuestionIndex(-1);
    }
  }, [open]);

  const handleAddQuestion = () => {
    setQuestions([...questions, { question: '', answer: '', isOpen: false }]);
    setOpenQuestionIndex(questions.length);
  };

  const toggleQuestion = (index: number) => {
    setOpenQuestionIndex(openQuestionIndex === index ? -1 : index);
  };

  const handleQuestionChange = (
    index: number,
    field: 'question' | 'answer',
    value: string
  ) => {
    const newQuestions = questions.map((q, i) => {
      if (i === index) {
        return { ...q, [field]: value };
      }
      return q;
    });
    setQuestions(newQuestions);
  };

  const handleDeleteQuestion = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
    if (openQuestionIndex === index) {
      setOpenQuestionIndex(-1);
    } else if (openQuestionIndex > index) {
      setOpenQuestionIndex(openQuestionIndex - 1);
    }
  };

  const handleSave = () => {
    try {
      setIsSaving(true);
      const validQuestions = questions.filter(q => q.question && q.answer);
      if (validQuestions.length === 0) {
        showAlert('Please add at least one question', 'error');
        setIsSaving(false);
        return;
      }

      const questionsWithIds = validQuestions.map((q, index) => ({
        ...q,
        id: selectedStore.existingQuestionsCount + index + 1,
      }));

      onSave(questionsWithIds);
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving questions:', error);
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '600px',
          padding: '20px 24px',
          backgroundColor: '#FAFBFD',
          overflow: 'auto',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': { backgroundColor: '#E8EBF1' },
              transition: 'all 0.2s ease',
            }}
          >
            <X size={18} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: 'custom.textBlue',
              letterSpacing: '-0.2px',
            }}
          >
            Create Questionnaire
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Plus size={16} />}
            onClick={handleAddQuestion}
            sx={{
              height: '34px',
              textTransform: 'none',
              fontSize: '14px',
              color: 'primary.main',
              borderColor: 'primary.main',
              borderRadius: '8px',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(90, 76, 230, 0.04)',
              },
            }}
          >
            Add Question
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || isPending}
            sx={{
              height: '34px',
              fontSize: 14,
              fontWeight: 500,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {isSaving || isPending ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)', mb: 2, mx: -3 }} />

      {/* Questions Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {questions.map((qa, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: '#DCE0E7',
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                bgcolor: '#5A4CE61A',
                cursor: 'pointer',
              }}
              onClick={() => toggleQuestion(index)}
            >
              <Typography
                sx={{ fontSize: '14px', fontWeight: 500, color: '#1C1C1C' }}
              >
                Question {selectedStore.existingQuestionsCount + index + 1}
              </Typography>
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <IconButton
                  onClick={e => handleDeleteQuestion(index, e)}
                  size="small"
                  sx={{
                    minWidth: '30px',
                    height: '30px',
                    p: 0,
                    borderRadius: '6px',
                    color: '#E74C3C',
                    bgcolor: '#E74C3C26',
                    '&:hover': { bgcolor: '#E74C3C33' },
                  }}
                >
                  <TrashSimple weight="fill" size={16} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    minWidth: '30px',
                    height: '30px',
                    p: 0,
                    borderRadius: '100%',
                    bgcolor: '#FEFEFE',
                    '&:hover': { bgcolor: '#FEFEEE' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {openQuestionIndex === index ? (
                    <CaretUp size={16} />
                  ) : (
                    <CaretDown size={16} />
                  )}
                </IconButton>
              </Box>
            </Box>

            {openQuestionIndex === index && (
              <Box
                sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '100%',
                      letterSpacing: '-1%',
                      color: 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    Question
                  </Typography>
                  <TextareaAutosize
                    placeholder="Enter your question here"
                    value={qa.question}
                    onChange={e =>
                      handleQuestionChange(index, 'question', e.target.value)
                    }
                    minRows={1}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #DCE0E7',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      resize: 'none',
                      outline: 'none',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '100%',
                      letterSpacing: '-1%',
                      color: 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    Answer
                  </Typography>
                  <TextareaAutosize
                    placeholder="Enter your answer here"
                    value={qa.answer}
                    onChange={e =>
                      handleQuestionChange(index, 'answer', e.target.value)
                    }
                    minRows={1}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #DCE0E7',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      resize: 'none',
                      outline: 'none',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    </Drawer>
  );
};

export default QuestionnaireDrawer;
